'use client'
import { useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'
import { TravelersSelector } from './travelersSelector'

interface TravelersSelectorWrapperProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    adults: number
    children: number
    infants: number
    flightClass: string
    onTravelerChange: (type: 'adults' | 'childs' | 'infants', delta: number) => void
    onFlightClassChange: (newClass: string) => void
    canIncreaseAdults: boolean
    canIncreaseChildren: boolean
    canIncreaseInfants: boolean
    canDecreaseAdults: boolean
    canDecreaseChildren: boolean
    canDecreaseInfants: boolean
    onApply: () => void
}

const TravelersSelectorWrapper: React.FC<TravelersSelectorWrapperProps> = ({
    open,
    onOpen,
    onClose,
    adults,
    children,
    infants,
    flightClass,
    onTravelerChange,
    onFlightClassChange,
    canIncreaseAdults,
    canIncreaseChildren,
    canIncreaseInfants,
    canDecreaseAdults,
    canDecreaseChildren,
    canDecreaseInfants,
    onApply
}) => {
    // Process traveler types data
    const t = useTranslations('common')
    const travelerTypes = useMemo(
        () => [
            {
                type: 'adults' as const,
                label: t('adult'),
                subLabel: t('adultSubLabel'),
                value: adults,
                canIncrease: canIncreaseAdults,
                canDecrease: canDecreaseAdults
            },
            {
                type: 'childs' as const,
                label: t('child'),
                subLabel: t('childSubLabel'),
                value: children,
                canIncrease: canIncreaseChildren,
                canDecrease: canDecreaseChildren
            },
            {
                type: 'infants' as const,
                label: t('infant'),
                subLabel: t('infantSubLabel'),
                value: infants,
                canIncrease: canIncreaseInfants,
                canDecrease: canDecreaseInfants
            }
        ],
        [
            adults,
            children,
            infants,
            canIncreaseAdults,
            canIncreaseChildren,
            canIncreaseInfants,
            canDecreaseAdults,
            canDecreaseChildren,
            canDecreaseInfants
        ]
    )

    // Flight class options
    const flightClasses = useMemo(
        () => [
            { value: 'Economy', label: t('Economy') },
            { value: 'Business', label: t('Business') },
            { value: 'First', label: t('First') },
            { value: 'PREMIUM_ECONOMY', label: t('PREMIUM_ECONOMY') }
        ],
        []
    )

    // Handle apply and close
    const handleApply = useCallback(() => {
        onApply()
        onClose()
    }, [onApply, onClose])

    // Handle traveler increment
    const handleIncrement = useCallback(
        (type: 'adults' | 'childs' | 'infants') => {
            onTravelerChange(type, 1)
        },
        [onTravelerChange]
    )

    // Handle traveler decrement
    const handleDecrement = useCallback(
        (type: 'adults' | 'childs' | 'infants') => {
            onTravelerChange(type, -1)
        },
        [onTravelerChange]
    )

    return (
        <TravelersSelector
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            travelerTypes={travelerTypes}
            flightClasses={flightClasses}
            flightClass={flightClass}
            onFlightClassChange={onFlightClassChange}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onApply={handleApply}
            t={t}
        />
    )
}

export { TravelersSelectorWrapper as TravelersSelector }
export default TravelersSelectorWrapper
